import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface Message {
  id: number;
  content: string;
  senderId: number;
  receiverId: number;
  matchId: number;
  read: boolean;
  createdAt: string;
  sender?: {
    id: number;
    name: string;
    email: string;
  };
}

interface SocketContextType {
  socket: Socket | null;
  connected: boolean;
  joinMatch: (matchId: number) => void;
  sendMessage: (matchId: number, content: string) => void;
  markAsRead: (matchId: number) => void;
  startTyping: (matchId: number) => void;
  stopTyping: (matchId: number) => void;
  onNewMessage: (callback: (message: Message) => void) => void;
  onTyping: (callback: (data: { userId: number; matchId: number }) => void) => void;
  onStopTyping: (callback: (data: { userId: number; matchId: number }) => void) => void;
  onMessagesRead: (callback: (data: { matchId: number; readBy: number }) => void) => void;
  onUserStatusChanged: (callback: (data: { userId: number; status: string; timestamp: Date }) => void) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const { token, user } = useAuth();

  useEffect(() => {
    if (token && user) {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const socketUrl = apiUrl.replace('/api', '');

      const newSocket = io(socketUrl, {
        auth: {
          token: token,
        },
      });

      newSocket.on('connect', () => {
        console.log('Socket connected');
        setConnected(true);
        // Update status to online
        newSocket.emit('update_status', 'online');
      });

      newSocket.on('disconnect', () => {
        console.log('Socket disconnected');
        setConnected(false);
      });

      newSocket.on('error', (error: any) => {
        console.error('Socket error:', error);
      });

      setSocket(newSocket);

      return () => {
        newSocket.emit('update_status', 'offline');
        newSocket.disconnect();
      };
    }
  }, [token, user]);

  const joinMatch = (matchId: number) => {
    if (socket) {
      socket.emit('join_match', matchId);
    }
  };

  const sendMessage = (matchId: number, content: string) => {
    if (socket && content.trim()) {
      socket.emit('send_message', { matchId, content });
    }
  };

  const markAsRead = (matchId: number) => {
    if (socket) {
      socket.emit('mark_read', { matchId });
    }
  };

  const startTyping = (matchId: number) => {
    if (socket) {
      socket.emit('typing_start', { matchId });
    }
  };

  const stopTyping = (matchId: number) => {
    if (socket) {
      socket.emit('typing_stop', { matchId });
    }
  };

  const onNewMessage = (callback: (message: Message) => void) => {
    if (socket) {
      socket.on('new_message', callback);
    }
  };

  const onTyping = (callback: (data: { userId: number; matchId: number }) => void) => {
    if (socket) {
      socket.on('user_typing', callback);
    }
  };

  const onStopTyping = (callback: (data: { userId: number; matchId: number }) => void) => {
    if (socket) {
      socket.on('user_stopped_typing', callback);
    }
  };

  const onMessagesRead = (callback: (data: { matchId: number; readBy: number }) => void) => {
    if (socket) {
      socket.on('messages_read', callback);
    }
  };

  const onUserStatusChanged = (callback: (data: { userId: number; status: string; timestamp: Date }) => void) => {
    if (socket) {
      socket.on('user_status_changed', callback);
    }
  };

  return (
    <SocketContext.Provider
      value={{
        socket,
        connected,
        joinMatch,
        sendMessage,
        markAsRead,
        startTyping,
        stopTyping,
        onNewMessage,
        onTyping,
        onStopTyping,
        onMessagesRead,
        onUserStatusChanged,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
